import {endOfDay} from 'date-fns/endOfDay';
import {startOfDay} from 'date-fns/startOfDay';

import {TCalendarRangeValue} from '../index';
import {ECalendarLeadDate, ECalendarType, TCalendarDate, TCalendarType} from '../types';

export const isAvailableDate = (date: Date, minDate?: Date | null, maxDate?: Date | null) => {
  return (!minDate || date >= minDate) && (!maxDate || date <= maxDate);
};

export const isRangeValue = (value?: TCalendarDate | null): value is TCalendarRangeValue =>
  !!value && !(value instanceof Date);

type TCalendarSettings = {
  startDate: Date | null;
  endDate: Date | null;
  calendarType: TCalendarType;
  minDate: Date | null;
  maxDate: Date | null;
  leadDate: ECalendarLeadDate;
};

const getOrderedPeriod = (
  nextDate: Date,
  {calendarType, startDate, endDate, leadDate}: TCalendarSettings,
): Pick<TCalendarSettings, 'startDate' | 'endDate'> => {
  if (calendarType === ECalendarType.DATE_RANGE && startDate && endDate) {
    return leadDate === ECalendarLeadDate.START
      ? {
          startDate: nextDate,
          endDate: null,
        }
      : {
          startDate: null,
          endDate: nextDate,
        };
  }

  if (leadDate === ECalendarLeadDate.END) {
    return !startDate || nextDate > startDate
      ? {
          startDate,
          endDate: nextDate,
        }
      : {
          startDate: nextDate,
          endDate: startDate,
        };
  }

  return !endDate || nextDate < endDate
    ? {
        startDate: nextDate,
        endDate,
      }
    : {
        startDate: endDate,
        endDate: nextDate,
      };
};

const getValidPeriod = ({
  startDate,
  endDate,
  minDate,
  maxDate,
}: TCalendarSettings): Pick<TCalendarSettings, 'startDate' | 'endDate'> => {
  const nextDates = {startDate, endDate};

  if (minDate && startDate && startDate <= minDate) {
    nextDates.startDate = minDate;
  }

  if (maxDate && endDate && endDate > maxDate) {
    nextDates.endDate = maxDate;
  }

  return nextDates;
};

export const getAvailableDate = (nextDate: Date, settings: TCalendarSettings) => {
  const orderedDays = getOrderedPeriod(nextDate, settings);
  const validDays = getValidPeriod({...settings, ...orderedDays});

  return {
    startDate: validDays.startDate ? startOfDay(validDays.startDate) : null,
    endDate: validDays.endDate ? endOfDay(validDays.endDate) : null,
  };
};
